import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { api } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { Refrigerator } from "lucide-react";
import { todayIso } from "@/lib/date";

const LS_NOTIFIED_KEY = "solano_pantry_notif_date";

/**
 * Pantry icon in the mobile top bar. Polls expiring items and:
 *  - Shows a red badge with the count
 *  - Fires a native browser Notification once per day (with permission)
 */
export default function PantryIconLink() {
  const { user } = useAuth();
  const { t } = useT();
  const [count, setCount] = useState(0);
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (!user || user.role !== "client") return;
    let cancelled = false;

    const check = async () => {
      try {
        const { data } = await api.get("/client/pantry/expiring?within_days=1");
        if (cancelled) return;
        const c = Number(data?.count || 0);
        setCount(c);
        if (c > 0) maybeNotify(data.items || []);
      } catch {}
    };

    const maybeNotify = (items) => {
      if (notifiedRef.current) return;
      if (typeof Notification === "undefined") return;
      const today = todayIso();
      let lastDate = null;
      try { lastDate = localStorage.getItem(LS_NOTIFIED_KEY); } catch {}
      if (lastDate === today) return; // already notified today
      // If not yet granted, request quietly
      const send = () => {
        try {
          const first = items[0];
          const title = t("pantry.notifTitle");
          const body = items.length === 1
            ? t("pantry.notifBodyOne", { name: first.name })
            : t("pantry.notifBodyMany", { count: items.length, name: first.name });
          const n = new Notification(title, { body, tag: "solano-pantry", icon: "/logo192.png" });
          n.onclick = () => { window.focus(); window.location.href = "/app/dispensa"; };
          try { localStorage.setItem(LS_NOTIFIED_KEY, today); } catch {}
          notifiedRef.current = true;
        } catch {}
      };
      if (Notification.permission === "granted") return send();
      if (Notification.permission !== "denied") {
        Notification.requestPermission().then((p) => { if (p === "granted") send(); });
      }
    };

    check();
    const iv = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user, t]);

  return (
    <NavLink
      to="/app/dispensa"
      data-testid="mobile-pantry-link"
      className="relative h-8 w-8 rounded-full bg-[#5A8B8F]/10 text-[#2C5254] flex items-center justify-center hover:bg-[#5A8B8F]/20 transition"
      aria-label={t("nav.pantry")}
      title={t("nav.pantry")}
    >
      <Refrigerator className="h-3.5 w-3.5" />
      {count > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
          data-testid="pantry-badge-count"
        >
          {count > 9 ? "9+" : count}
        </span>
      )}
    </NavLink>
  );
}
